import React from "react";
import AsyncSelect from "react-select/async";
import { buildSelectStyles } from "../utils/selectStyles";

/**
 * DataMappingMultiSelect Component
 *
 * A reusable multi-select dropdown component using react-select with multi-select
 * and chip display functionality for the Data Mapping page.
 *
 * Props:
 * - loadOptions: Function to load options asynchronously
 * - selectedValues: Array of selected values
 * - onChange: Function to handle selection changes (values: string[]) => void
 * - placeholder: Optional placeholder text
 * - isDisabled: Optional boolean to disable the dropdown
 * - className: Optional CSS class name
 */
const DataMappingMultiSelect = ({
  loadOptions = [],
  selectedValues = [],
  onChange,
  placeholder = "Select options...",
  isDisabled = false,
  className = "",
}) => {
  const loadingMessage = "Loading Tables...";

  // Ensure selectedValues is always an array and filter out invalid values
  const validSelectedValues = Array.isArray(selectedValues)
    ? selectedValues.filter(
        value => value && typeof value === "string" && value.trim() !== ""
      )
    : [];

  // Convert selectedValues array to react-select format
  const selectedOptions = validSelectedValues.map(value => ({
    value: value,
    label: value,
  }));

  // Handle selection changes
  const handleChange = selectedOptions => {
    if (!selectedOptions) {
      onChange([]);
      return;
    }

    // Extract values from selected options
    const values = selectedOptions.map(option => option.value);
    onChange(values);
  };

  // Shared react-select styling — see utils/selectStyles.js.
  const customStyles = buildSelectStyles({ isDisabled });

  return (
    <div className={`data-mapping-multiselect-container ${className}`}>
      <AsyncSelect
        isMulti
        closeMenuOnSelect={false}
        loadOptions={async inputValue => {
          if (typeof loadOptions !== "function") {
            console.error(
              "DataMappingMultiSelect - loadOptions is not a function:",
              loadOptions
            );
            return [];
          }
          try {
            const result = await loadOptions(inputValue);
            return result;
          } catch (error) {
            console.error("DataMappingMultiSelect - loadOptions error:", error);
            return [];
          }
        }}
        value={selectedOptions}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        className={className || "data-mapping-multiselect"}
        classNamePrefix="data-mapping-multiselect"
        noOptionsMessage={() => "No options available"}
        loadingMessage={() => loadingMessage}
        cacheOptions={true}
        defaultOptions={true}
      />
    </div>
  );
};

export default DataMappingMultiSelect;
